import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/** A gull over the Bosphorus. */
interface Gull {
  x: number;
  y: number;
  base: number;
  dir: 1 | -1;
  speed: number;
  size: number;
  bob: number;
  bobRate: number;
  phase: number;
  flap: number;
  rank: number;
  shown: number;
}

/** A boat on the water: a sailboat or one of the city's ferries. */
interface Boat {
  x: number;
  y: number;
  dir: 1 | -1;
  speed: number;
  size: number;
  ferry: boolean;
  sail: RGB;
  /** The stretch of open water across its lane, between the two shores. */
  minX: number;
  maxX: number;
  /** Out only while the counter is strong enough for its rank; fading in and out. */
  rank: number;
  shown: number;
}

/** A car, bus or lorry crossing the bridge, a dark silhouette on the deck. */
interface Car {
  m: number;
  dir: 1 | -1;
  speed: number;
  len: number;
  tall: number;
  colour: string;
  rank: number;
  shown: number;
}

/** A glint of sun on the water. */
interface Glint {
  x: number;
  y: number;
  len: number;
  rate: number;
  phase: number;
}

/**
 * Istanbul's hero board (2026-10-17): the Bosphorus Bridge seen from the water, in true perspective (a
 * camera 1600 m off the bridge and 30 m up; the real spans and heights), centred and sized by the
 * stage's height. A blue sky with level clouds drifting right; the turquoise strait running under the
 * bridge to the horizon between its two shores; the towers on their foundations by the quays, their legs
 * one behind the other with the gently rising deck between them, traffic crossing it in silhouette;
 * yalıs and the Çırağan and Beylerbeyi palaces along the water, houses and trees up the hills behind;
 * white sailboats and the city's ferries each on a lane of its own; the gulls of Dock Town's Istanbul,
 * which the pointer, a finger or the counter counting bring out in numbers (only they, owner). The
 * page's wallpaper is the gulls alone.
 */
@Component( {
  selector: 'app-hero-istanbul',
  template: '<canvas #canvas class="hist-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hist-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroIstanbulComponent extends HeroMotifBase {

  private back: HTMLCanvasElement | null = null;
  private front: HTMLCanvasElement | null = null;
  private clouds: HTMLCanvasElement | null = null;
  /** The Maiden's Tower on its islet, on its own layer, and the waterline it stands at (for depth order with the boats). */
  private maiden: HTMLCanvasElement | null = null;
  private maidenY = 0;
  private cars: Car[] = [];
  private cloudOff = 0;
  /** The bridge's projection and deck height, kept from the bake for the traffic. */
  private project: ( ( x: number, y: number, z: number ) => [ number, number ] ) | null = null;
  private deckAtM: ( m: number ) => number = () => 64;
  private gulls: Gull[] = [];
  private boats: Boat[] = [];
  private glints: Glint[] = [];
  private scaleK = 1;
  private UW = 0;
  private horizon = 0;
  /** The European shore's edge at the left: at each height, the x where the water begins. */
  private shoreX: ( y: number ) => number = () => 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
    this.front = null;
  }

  protected build(): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    // sized by the stage's height alone, centred, so a wide screen never stretches the bridge (owner)
    this.UW = H * 1.75;
    // the strait seen straight across, the bridge in the middle (owner): the horizon low enough for the
    // bridge to stand over the hero's words
    // the whole scene 10 px up (owner)
    this.horizon = H * 0.62 - 10;
    this.shoreX = () => 0;
    this.back = null;
    this.front = null;
    const r = this.seededLocal( 17 );
    // the gulls: a pool, each out only while the counter is strong enough for its rank
    this.gulls = [];
    for ( let i = 0; i < 28; i++ ) {
      const far = r();
      this.gulls.push( {
        x: r() * W, y: 0, base: H * ( 0.06 + 0.32 * r() ), dir: r() < 0.5 ? 1 : -1,
        speed: ( 45 + r() * 40 ) * k * ( 1 - 0.4 * far ), size: ( 22 - 10 * far ) * k,
        bob: ( 10 + r() * 24 ) * k, bobRate: 0.25 + r() * 0.35, phase: r() * Math.PI * 2,
        flap: r() * 10, rank: i === 0 ? 0 : r(), shown: 0,
      } );
    }
    // the boats: sailboats and two ferries, smaller and slower far off
    this.boats = [];
    // sailboats, most of their sails white, a few red, and three ferries; nearer = bigger
    // fewer sailboats (owner), three ferries
    // each its own lane across the water, so no two pass at the same height and run into each other
    // (owner): the lanes spread evenly from far to near, handed out in a fixed shuffled order
    // eleven always out, and eleven more the counter brings out, each on a lane of its own between
    // theirs (owner: more and faster on the charge)
    const lanes = [ 3, 8, 1, 10, 5, 0, 7, 2, 9, 4, 6 ];
    for ( let i = 0; i < 22; i++ ) {
      const extra = i >= 11;
      const lane = extra ? Math.min( 21, lanes[ i - 11 ] * 2 + 1 ) : lanes[ i ] * 2;
      const depth = Math.pow( ( lane + 0.5 ) / 22, 0.9 );
      // not too far off, where the strait narrows between its shores (owner: they sailed on land)
      const y = this.horizon + ( H - this.horizon ) * ( 0.16 + depth * 0.8 );
      // the shores at this lane's distance, in the bridge's perspective (camera 1600 m off, 30 m up)
      const Fp = this.UW * 0.4 * 1600 / 537;
      const dist = Fp * 30 / Math.max( 1, y - this.horizon );
      const half = Fp * 515 / dist;
      const ferry = i < 3 || i === 12 || i === 16;
      // the ferries 10% bigger (owner)
      const sizeB = ( ferry ? ( 14 + 60 * depth ) * 1.1 : 6 + 30 * depth ) * k;
      const minX = Math.max( -sizeB * 3, W / 2 - half + sizeB * 1.2 ), maxX = Math.min( W + sizeB * 3, W / 2 + half - sizeB * 1.2 );
      const sx = minX;
      r(); // (kept so the boats keep their places)
      this.boats.push( {
        x: sx + r() * ( maxX - sx ), y, dir: r() < 0.5 ? 1 : -1, minX, maxX,
        speed: ( 3 + 9 * depth ) * k, size: sizeB, ferry, rank: extra ? 0.1 + r() * 0.9 : 0, shown: extra ? 0 : 1,
        // white sails only (owner)
        sail: [ 250, 250, 248 ],
      } );
    }
    this.boats.sort( ( a, b ) => a.y - b.y );
    // the glints on the water
    this.glints = [];
    for ( let i = 0; i < 180; i++ ) {
      const depth = Math.pow( r(), 1.3 );
      this.glints.push( { x: r() * W, y: this.horizon + ( H - this.horizon ) * ( 0.01 + depth * 0.97 ), len: ( 3 + 10 * depth ) * k, rate: 1 + Math.floor( r() * 4 ), phase: r() * 6.28 } );
    }
    // the traffic on the bridge: cars mostly, a few buses and lorries, both ways
    this.cars = [];
    for ( let i = 0; i < 40; i++ ) {
      const big = r() < 0.18;
      this.cars.push( {
        m: -2500 + r() * 5000, dir: i % 2 ? 1 : -1, speed: 16 + r() * 8,
        // a little larger than life, so they read at this distance (15% smaller than first, owner)
        len: ( big ? 11 + r() * 5 : 4.2 + r() * 1.2 ) * 1.53, tall: ( big ? 3.2 + r() * 0.6 : 1.5 + r() * 0.3 ) * 1.53,
        // buses grey or in İETT's lime yellow, with their dark band of windows (owner); red, blue and the
        // taxis' yellow are for cars
        colour: big ? ( r() < 0.6 ? 'rgb(222,212,62)' : 'rgb(170,176,182)' )
          // most of them white, grey or black (owner), now and then a red, a blue or a yellow taxi
          : ( ( q: number ) => q < 0.3 ? 'rgb(240,240,236)' : q < 0.55 ? 'rgb(170,176,182)' : q < 0.8 ? 'rgb(44,46,52)'
            : q < 0.87 ? 'rgb(196,58,52)' : q < 0.93 ? 'rgb(64,104,160)' : 'rgb(236,190,40)' )( r() ),
        rank: i < 8 ? i / 80 : r(), shown: 0,
      } );
    }
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    if ( this.frozen ) {
      // the page's wallpaper: the gulls alone, still, all down the page
      this.drawWallGulls( ctx );
      return;
    }
    if ( !this.back ) { this.bake( ctx.getTransform().a ); }
    if ( !this.back || !this.front ) { return; }
    ctx.drawImage( this.back, 0, 0, W, H );
    if ( this.clouds ) {
      // drifting slowly right
      if ( dt > 0 ) { this.cloudOff = ( this.cloudOff + dt * 8 * this.scaleK ) % W; }
      const off = Math.round( this.cloudOff );
      ctx.drawImage( this.clouds, off, 0, W, H );
      ctx.drawImage( this.clouds, off - W, 0, W, H );
    }
    this.drawCars( ctx, dt );
    this.drawGlints( ctx );
    this.drawBoats( ctx, dt );
    ctx.drawImage( this.front, 0, 0, W, H );
    this.drawGulls( ctx, dt );
  }

  /**
   * The traffic crossing the bridge: dark silhouettes of cars, buses and lorries on the deck, going both
   * ways, a few at rest, many and quicker when the counter counts; out of sight where a tower's legs
   * stand.
   */
  private drawCars( ctx: CanvasRenderingContext2D, dt: number ): void {
    const P = this.project;
    if ( !P ) { return; }
    // the counter brings out only the gulls (owner): the traffic stays as it is
    const share = 0.3;
    for ( const c of this.cars ) {
      const want = c.rank <= share ? 1 : 0;
      c.shown = dt > 0 ? c.shown + ( want - c.shown ) * Math.min( 1, dt * 0.8 ) : want;
      if ( dt > 0 ) {
        c.m += c.dir * c.speed * dt;
        if ( c.m > 2600 ) { c.m = -2600; } else if ( c.m < -2600 ) { c.m = 2600; }
      }
      if ( c.shown < 0.02 || Math.abs( Math.abs( c.m ) - 537 ) < 10 ) { continue; }
      const z = c.dir > 0 ? -5 : 5;
      const y0 = this.deckAtM( c.m );
      const [ x0, yb ] = P( c.m - c.len / 2, y0, z ), [ x1 ] = P( c.m + c.len / 2, y0, z ), [ , yt ] = P( c.m, y0 + c.tall, z );
      const h = yb - yt, w = x1 - x0;
      if ( w < 0.3 ) { continue; }
      ctx.globalAlpha = 0.9 * c.shown;
      ctx.fillStyle = c.colour;
      ctx.beginPath();
      if ( c.tall > 4.5 ) {
        ctx.rect( x0, yt, w, h );
        ctx.fill();
        // the dark band of windows along its side
        ctx.fillStyle = 'rgb(40,44,50)';
        ctx.fillRect( x0 + w * 0.04, yt + h * 0.18, w * 0.92, h * 0.38 );
        continue;
      } else {
        ctx.moveTo( x0, yb );
        ctx.lineTo( x0, yb - h * 0.55 );
        ctx.lineTo( x0 + w * ( c.dir > 0 ? 0.25 : 0.15 ), yt );
        ctx.lineTo( x1 - w * ( c.dir > 0 ? 0.15 : 0.25 ), yt );
        ctx.lineTo( x1, yb - h * 0.55 );
        ctx.lineTo( x1, yb );
        ctx.closePath();
      }
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  /** The sun glinting on the water: soft elongated sparkles coming and going on the loop. */
  private drawGlints( ctx: CanvasRenderingContext2D ): void {
    const t = this.time * W0;
    ctx.save();
    for ( const g of this.glints ) {
      const a = Math.max( 0, Math.sin( t * g.rate + g.phase ) );
      if ( a < 0.05 ) { continue; }
      ctx.save();
      ctx.translate( g.x, g.y );
      ctx.scale( g.len, g.len * 0.18 );
      const gr = ctx.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      gr.addColorStop( 0, `rgba(255,255,255,${ 0.75 * a })` );
      gr.addColorStop( 1, 'rgba(255,255,255,0)' );
      ctx.fillStyle = gr;
      ctx.fillRect( -1, -1, 2, 2 );
      ctx.restore();
    }
    ctx.restore();
  }

  /** The boats going slowly up and down the Bosphorus, each with its soft wake. */
  private drawBoats( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    // the Maiden's Tower takes its place among the boats by its distance: drawn before the nearer ones
    let maidenDone = !this.maiden;
    // the boats stay as they are on the charge (owner: only the gulls)
    const share = 0.05;
    for ( const b of this.boats ) {
      if ( !maidenDone && b.y > this.maidenY ) { ctx.drawImage( this.maiden!, 0, 0, W, H ); maidenDone = true; }
      const want = b.rank <= share ? 1 : 0;
      if ( dt > 0 && want && b.shown < 0.02 ) { b.x = b.minX + Math.random() * ( b.maxX - b.minX ); }
      b.shown = dt > 0 ? b.shown + ( want - b.shown ) * Math.min( 1, dt * 0.6 ) : want;
      if ( b.shown < 0.02 ) { continue; }
      if ( dt > 0 ) {
        b.x += b.dir * b.speed * dt;
        // turning back at the shores, so they stay on the water
        if ( b.x > b.maxX ) { b.x = b.maxX; b.dir = -1; } else if ( b.x < b.minX ) { b.x = b.minX; b.dir = 1; }
      }
      ctx.save();
      ctx.globalAlpha = b.shown;
      ctx.translate( b.x, b.y );
      ctx.scale( b.dir, 1 );
      this.boat( ctx, b );
      ctx.restore();
    }
    if ( !maidenDone ) { ctx.drawImage( this.maiden!, 0, 0, W, H ); }
  }

  /** One boat facing right, its waterline at y 0: a sailboat's hull and white sail, or a ferry. */
  private boat( ctx: CanvasRenderingContext2D, b: Boat ): void {
    const [ , blue, cream, , red ] = this.five();
    const s = b.size;
    // the wake trailing behind
    const wk = ctx.createLinearGradient( -s * 2.4, 0, 0, 0 );
    wk.addColorStop( 0, 'rgba(255,255,255,0)' );
    wk.addColorStop( 1, 'rgba(255,255,255,0.55)' );
    ctx.fillStyle = wk;
    ctx.beginPath();
    ctx.moveTo( 0, s * 0.02 );
    ctx.quadraticCurveTo( -s * 1.2, s * 0.1, -s * 2.4, s * 0.16 );
    ctx.lineTo( -s * 2.4, s * 0.2 );
    ctx.quadraticCurveTo( -s * 1.2, s * 0.14, 0, s * 0.08 );
    ctx.fill();
    // its reflection, a soft darker blue under it
    ctx.fillStyle = this.rgba( this.mix( blue, [ 10, 60, 90 ], 0.3 ), 0.18 );
    ctx.beginPath();
    ctx.ellipse( 0, s * 0.08, s * 0.55, s * 0.06, 0, 0, Math.PI * 2 );
    ctx.fill();
    if ( b.ferry ) {
      // an Istanbul city ferry (vapur), after the owner's photograph (the Moda), its bow to the right: the
      // hull black below and white above, its sheer rising to a tall raked bow; a yellow line along the
      // deck edge and one lower down, both climbing toward the bow; two rows of small dark windows, a
      // glazed lounge forward; the superstructure amidships with the white funnel and the wheelhouse; the
      // two tall yellow masts with their stays; red lifebuoys on the rails; a trail
      // of smoke from the funnel
      const fillP = ( pts: number[][], fill: string ) => {
        ctx.fillStyle = fill;
        ctx.beginPath();
        pts.forEach( ( [ x, y ], i ) => i ? ctx.lineTo( x * s, y * s ) : ctx.moveTo( x * s, y * s ) );
        ctx.closePath();
        ctx.fill();
      };
      const whiteC = 'rgb(248,248,245)', shadeC = 'rgb(214,220,224)', glassC = 'rgb(38,44,52)', blackC = 'rgb(24,26,30)';
      const yellow = 'rgb(226,166,30)';
      // the deck line: low aft, rising toward the bow
      // nearly level to the bow, which does not point up (owner)
      const deckY = ( x: number ) => -0.2 - 0.025 * Math.pow( Math.max( 0, ( x + 0.2 ) / 1.2 ), 2 );
      const sheer: number[][] = [];
      for ( let x = -0.98; x <= 0.98; x += 0.07 ) { sheer.push( [ x, deckY( x ) ] ); }
      // the hull all white (owner), only a thin dark band at the waterline; the bow blunt and upright as
      // the first drawing had it, not a sharp point
      const sheerB = sheer.filter( ( [ x ] ) => x <= 0.9 );
      const bowTop = deckY( 0.9 );
      fillP( [ [ -0.98, 0.04 ], [ -1.0, -0.06 ], ...sheerB, [ 0.9, bowTop ], [ 1.0, bowTop - 0.02 ], [ 0.97, -0.05 ], [ 0.95, 0 ], [ 0.86, 0.06 ], [ -0.94, 0.06 ] ], whiteC );
      fillP( [ [ -0.97, 0.03 ], [ 0.92, 0.03 ], [ 0.86, 0.065 ], [ -0.94, 0.065 ] ], blackC );
      // the yellow lines
      ctx.strokeStyle = yellow;
      ctx.lineWidth = Math.max( 0.7, 0.012 * s );
      ctx.beginPath();
      for ( let x = -0.96; x <= 0.96; x += 0.05 ) { const y = deckY( x ) + 0.02; x <= -0.96 ? ctx.moveTo( x * s, y * s ) : ctx.lineTo( x * s, y * s ); }
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo( -0.96 * s, -0.075 * s );
      ctx.lineTo( 0.3 * s, -0.085 * s );
      ctx.quadraticCurveTo( 0.7 * s, -0.09 * s, 0.96 * s, -0.1 * s );
      ctx.stroke();
      // the lower row of windows, portholes forward
      for ( let wx = -0.9; wx < 0.3; wx += 0.05 ) { fillP( [ [ wx, -0.12 ], [ wx + 0.03, -0.12 ], [ wx + 0.03, -0.095 ], [ wx, -0.095 ] ], glassC ); }
      ctx.fillStyle = glassC;
      for ( const px of [ 0.45, 0.55, 0.65 ] ) { ctx.beginPath(); ctx.arc( px * s, ( deckY( px ) + 0.09 ) * s, 0.012 * s, 0, Math.PI * 2 ); ctx.fill(); }
      // the upper deck: its cabin, the row of windows, the glazed lounge forward
      fillP( [ [ -0.94, -0.2 ], [ 0.42, -0.215 ], [ 0.42, -0.31 ], [ -0.94, -0.3 ] ], whiteC );
      for ( let wx = -0.88; wx < -0.05; wx += 0.045 ) { fillP( [ [ wx, -0.27 ], [ wx + 0.028, -0.27 ], [ wx + 0.028, -0.24 ], [ wx, -0.24 ] ], glassC ); }
      fillP( [ [ 0.0, -0.285 ], [ 0.36, -0.285 ], [ 0.38, -0.245 ], [ 0.0, -0.245 ] ], glassC );
      // the rail along the upper deck, with its red lifebuoys
      ctx.strokeStyle = 'rgb(180,180,176)';
      ctx.lineWidth = Math.max( 0.5, 0.006 * s );
      ctx.beginPath(); ctx.moveTo( -0.94 * s, -0.31 * s ); ctx.lineTo( 0.42 * s, -0.315 * s ); ctx.stroke();
      ctx.fillStyle = this.rgba( this.mix( red, [ 255, 90, 50 ], 0.3 ), 1 );
      for ( const bx of [ -0.6, 0.06, 0.12, 0.18, 0.24, 0.3 ] ) { ctx.beginPath(); ctx.arc( bx * s, -0.235 * s, 0.014 * s, 0, Math.PI * 2 ); ctx.fill(); }
      // the superstructure amidships and the wheelhouse with its dark windows
      fillP( [ [ -0.32, -0.31 ], [ 0.2, -0.31 ], [ 0.24, -0.4 ], [ -0.3, -0.4 ] ], whiteC );
      fillP( [ [ -0.06, -0.385 ], [ 0.2, -0.385 ], [ 0.22, -0.35 ], [ -0.06, -0.35 ] ], glassC );
      fillP( [ [ -0.32, -0.31 ], [ 0.2, -0.31 ], [ 0.2, -0.32 ], [ -0.32, -0.32 ] ], shadeC );
      // the funnel: white, a yellow band at its foot, a dark rim, the emblem
      fillP( [ [ -0.3, -0.4 ], [ -0.16, -0.4 ], [ -0.17, -0.5 ], [ -0.29, -0.5 ] ], whiteC );
      fillP( [ [ -0.3, -0.4 ], [ -0.16, -0.4 ], [ -0.16, -0.425 ], [ -0.3, -0.425 ] ], yellow );
      fillP( [ [ -0.29, -0.49 ], [ -0.17, -0.49 ], [ -0.17, -0.505 ], [ -0.29, -0.505 ] ], blackC );
      ctx.fillStyle = this.rgba( red, 1 );
      ctx.beginPath(); ctx.arc( -0.23 * s, -0.455 * s, 0.014 * s, 0, Math.PI * 2 ); ctx.fill();
      // the two tall yellow masts and a short one by the funnel, plain poles, with their stays
      ctx.strokeStyle = yellow;
      ctx.lineCap = 'round';
      ctx.lineWidth = Math.max( 0.8, 0.014 * s );
      ctx.beginPath();
      ctx.moveTo( -0.48 * s, -0.3 * s ); ctx.lineTo( -0.45 * s, -0.95 * s );
      ctx.moveTo( 0.62 * s, deckY( 0.62 ) * s ); ctx.lineTo( 0.63 * s, -0.97 * s );
      ctx.moveTo( -0.08 * s, -0.4 * s ); ctx.lineTo( -0.08 * s, -0.56 * s );
      ctx.stroke();
      ctx.strokeStyle = 'rgba(80,84,90,0.4)';
      ctx.lineWidth = Math.max( 0.4, 0.004 * s );
      ctx.beginPath();
      ctx.moveTo( -0.45 * s, -0.93 * s ); ctx.lineTo( -0.9 * s, -0.3 * s );
      ctx.moveTo( -0.45 * s, -0.93 * s ); ctx.lineTo( -0.2 * s, -0.4 * s );
      ctx.moveTo( 0.63 * s, -0.95 * s ); ctx.lineTo( 0.99 * s, bowTop * s );
      ctx.moveTo( 0.63 * s, -0.95 * s ); ctx.lineTo( 0.35 * s, -0.31 * s );
      ctx.stroke();
      // smoke from the funnel, drifting back
      for ( let i = 0; i < 6; i++ ) {
        const px = ( -0.25 - i * 0.07 ) * s, py = ( -0.53 - i * 0.03 ) * s, pr = ( 0.04 + i * 0.02 ) * s;
        const sg = ctx.createRadialGradient( px, py, 0, px, py, pr );
        sg.addColorStop( 0, `rgba(70,70,74,${ 0.3 - i * 0.04 })` );
        sg.addColorStop( 1, 'rgba(70,70,74,0)' );
        ctx.fillStyle = sg;
        ctx.fillRect( px - pr, py - pr, pr * 2, pr * 2 );
      }
    } else {
      // a sailboat: a white hull, the mast, the mainsail and the jib, lit on the side toward the sun
      ctx.fillStyle = 'rgb(248,248,246)';
      ctx.beginPath();
      ctx.moveTo( -0.36 * s, 0 );
      ctx.lineTo( 0.3 * s, 0 );
      ctx.lineTo( 0.44 * s, -0.1 * s );
      ctx.lineTo( -0.4 * s, -0.1 * s );
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = this.rgba( [ 40, 60, 80 ], 0.35 );
      ctx.fillRect( -0.36 * s, -0.03 * s, 0.68 * s, 0.03 * s );
      ctx.fillStyle = this.rgba( [ 90, 90, 90 ], 0.9 );
      ctx.fillRect( -0.01 * s, -1.05 * s, Math.max( 0.6, 0.02 * s ), 0.95 * s );
      const sail = ctx.createLinearGradient( -0.3 * s, 0, 0, 0 );
      sail.addColorStop( 0, this.rgba( this.mix( b.sail, [ 150, 165, 180 ], 0.18 ), 1 ) );
      sail.addColorStop( 1, this.rgba( b.sail, 1 ) );
      ctx.fillStyle = sail;
      ctx.beginPath();
      ctx.moveTo( -0.02 * s, -1.02 * s );
      ctx.quadraticCurveTo( -0.2 * s, -0.55 * s, -0.32 * s, -0.14 * s );
      ctx.lineTo( -0.02 * s, -0.14 * s );
      ctx.closePath();
      ctx.fill();
      // the jib always white: a boat's sails are all white, or red and white (owner)
      ctx.fillStyle = 'rgb(248,249,248)';
      ctx.beginPath();
      ctx.moveTo( 0.02 * s, -0.9 * s );
      ctx.quadraticCurveTo( 0.16 * s, -0.5 * s, 0.34 * s, -0.13 * s );
      ctx.lineTo( 0.02 * s, -0.13 * s );
      ctx.closePath();
      ctx.fill();
    }
  }

  /**
   * The gulls wheeling over the Bosphorus, gliding long and beating now and then, a few at rest, many
   * when the counter counts; called out somewhere and fading in, climbing away as they go.
   */
  private drawGulls( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const e = this.energy;
    const share = 0.08 + 0.92 * e;
    for ( const g of this.gulls ) {
      const want = g.rank <= share ? 1 : 0;
      if ( dt > 0 && want && g.shown < 0.02 ) { g.x = Math.random() * W; }
      g.shown = dt > 0 ? g.shown + ( want - g.shown ) * Math.min( 1, dt * 0.55 ) : want;
      if ( dt > 0 ) {
        g.x += g.dir * g.speed * ( 1 + 0.5 * e ) * dt;
        g.phase += dt * g.bobRate;
        g.flap += dt;
        const m = g.size * 3;
        if ( g.x > W + m ) { g.x = -m; } else if ( g.x < -m ) { g.x = W + m; }
      }
      if ( g.shown < 0.02 ) { continue; }
      // a gull soars: a slow rise and fall on the wind
      g.y = g.base + Math.sin( g.phase * 2 ) * g.bob;
      // long glides, a few slow deep beats between them, in two parts (the arm leads, the hand lags)
      const glide = Math.min( 1, Math.max( 0, ( Math.sin( g.flap * 0.6 ) + 0.1 ) / 0.4 ) );
      const theta = g.flap * Math.PI * 2 * 1.6;
      const arm = Math.sin( theta ) * ( 1 - glide ) - 0.15 * glide;
      const hand = Math.sin( theta - 0.8 ) * ( 1 - glide ) - 0.1 * glide;
      const away = 1 - g.shown;
      ctx.save();
      ctx.globalAlpha = Math.pow( g.shown, 0.7 );
      ctx.translate( g.x, g.y - away * g.bob * 2.5 );
      ctx.scale( ( 1 - 0.55 * away ) * g.dir, 1 - 0.55 * away );
      this.gull( ctx, g.size, arm, hand );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * One gull facing right (y down), as Dock Town draws it: long narrow wings in two parts, the arm to
   * the wrist at `arm` and the hand to the tip at `hand` (-1 raised, 0 out, 1 lowered), pale grey above,
   * whiter beneath while raised, their tips dark slate; the white body, the short tail, the head with
   * its yellow bill.
   */
  private gull( ctx: CanvasRenderingContext2D, s: number, arm: number, hand: number ): void {
    const [ , blue ] = this.five();
    const pose = ( a: number[], t: number ) => t <= 0 ? a[ 1 ] + ( a[ 0 ] - a[ 1 ] ) * -t : a[ 1 ] + ( a[ 2 ] - a[ 1 ] ) * t;
    const bendY = pose( [ -0.42, -0.3, 0.02 ], arm );
    const tipY = pose( [ -0.82, -0.2, 0.4 ], hand ), tipX = pose( [ 0.86, 1.04, 0.92 ], hand );
    const grey: RGB = this.mix( [ 196, 206, 214 ], blue, 0.12 );
    const wingC = this.mix( grey, [ 244, 246, 244 ], Math.max( 0, -hand ) * 0.7 );
    const tipC: RGB = this.mix( [ 52, 60, 68 ], blue, 0.15 );
    const fill = ( pts: number[][], c: RGB ) => {
      ctx.fillStyle = this.rgba( c, 1 );
      ctx.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? ctx.lineTo( x * s, y * s ) : ctx.moveTo( x * s, y * s ) );
      ctx.closePath();
      ctx.fill();
    };
    for ( const side of [ -1, 1 ] ) {
      const sh = [ side * 0.1, -0.06 ], bk = [ side * 0.08, 0.12 ];
      const bend = [ side * 0.46, bendY ], end = [ side * tipX, tipY ], trail = [ side * 0.5, bendY + 0.2 ];
      fill( [ sh, bend, end, trail, bk ], wingC );
      const a = [ bend[ 0 ] + ( end[ 0 ] - bend[ 0 ] ) * 0.62, bend[ 1 ] + ( end[ 1 ] - bend[ 1 ] ) * 0.62 ];
      const b = [ trail[ 0 ] + ( end[ 0 ] - trail[ 0 ] ) * 0.62, trail[ 1 ] + ( end[ 1 ] - trail[ 1 ] ) * 0.62 ];
      fill( [ a, end, b ], tipC );
    }
    fill( [ [ -0.3, -0.04 ], [ -0.56, -0.1 ], [ -0.56, 0.08 ], [ -0.3, 0.08 ] ], [ 228, 233, 236 ] );
    ctx.fillStyle = 'rgb(250,251,249)';
    ctx.beginPath();
    ctx.ellipse( -0.02 * s, 0.02 * s, 0.34 * s, 0.13 * s, 0, 0, Math.PI * 2 );
    ctx.fill();
    ctx.beginPath();
    ctx.arc( 0.32 * s, -0.06 * s, 0.13 * s, 0, Math.PI * 2 );
    ctx.fill();
    fill( [ [ 0.42, -0.09 ], [ 0.6, -0.05 ], [ 0.42, -0.01 ] ], [ 242, 193, 78 ] );
  }

  /** The wallpaper: the gulls scattered still down the page, each caught at its own point in the beat. */
  private drawWallGulls( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const r = this.seededLocal( 5 );
    const n = Math.max( 4, Math.round( W * H / 70000 ) );
    for ( let i = 0; i < n; i++ ) {
      const th = r() * Math.PI * 2;
      ctx.save();
      ctx.globalAlpha = 0.55 + r() * 0.35;
      ctx.translate( r() * W, r() * H );
      ctx.scale( r() < 0.5 ? 1 : -1, 1 );
      this.gull( ctx, ( 12 + r() * 9 ) * k, Math.sin( th ), Math.sin( th - 0.8 ) );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * The still parts, painted once: the sky and its clouds, the far shore, the bridge and the water on
   * the back layer; the near shore at the left with the Ortaköy Mosque on its quay on the front one.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const UW = this.UW;
    const hz = this.horizon;
    const cx = W / 2;
    const [ aqua, blue, cream, terra, red ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g = c.getContext( '2d' )!;
      g.scale( scale, scale );
      return { c, g };
    };
    const { c, g } = canvas();
    // the sky: a clear blue high up, paling to the haze over the far hills
    const sky = g.createLinearGradient( 0, 0, 0, hz );
    sky.addColorStop( 0, this.rgba( this.mix( blue, white, 0.15 ), 1 ) );
    sky.addColorStop( 0.65, this.rgba( this.mix( blue, aqua, 0.55 ), 1 ) );
    sky.addColorStop( 1, this.rgba( this.mix( aqua, white, 0.6 ), 1 ) );
    g.fillStyle = sky;
    g.fillRect( 0, 0, W, hz + 2 );
    // the clouds on their own layer, drifting slowly right (owner), each bank repeated a stage-width to
    // either side so the drift wraps round unseen
    const cl = canvas();
    // the clouds: level banks of white heaped above, lying low over the far hills, a few higher up
    const cr = this.seededLocal( 29 );
    const banks: [ number, number, number ][] = [ [ -0.6, 0.3, 0.32 ], [ -0.15, 0.28, 0.3 ], [ 0.35, 0.3, 0.34 ], [ 0.85, 0.29, 0.3 ], [ -1.1, 0.3, 0.3 ], [ 1.3, 0.3, 0.3 ], [ -0.4, 0.1, 0.18 ], [ 0.55, 0.08, 0.22 ] ];
    for ( const [ u, v, bw ] of banks ) {
      const bx = cx + u * UW, by = H * v - 10;
      if ( bx < -UW * 0.3 || bx > W + UW * 0.3 ) { continue; }
      const puffs: [ number, number, number ][] = [];
      for ( let i = 0; i < 30; i++ ) {
        const a = cr() * 2 - 1;
        const heap = Math.sqrt( 1 - a * a );
        puffs.push( [ bx + a * bw * UW * 0.5, by - cr() * heap * H * 0.07, ( 0.4 + cr() * 0.5 ) * H * 0.045 * ( 0.6 + heap ) ] );
      }
      puffs.sort( ( p, q ) => q[ 1 ] - p[ 1 ] );
      for ( const [ px, py, pr ] of puffs ) {
        const up = Math.max( 0, Math.min( 1, ( by - py ) / ( H * 0.07 ) ) );
        const tone = this.mix( this.mix( [ 200, 214, 226 ], blue, 0.12 ), white, 0.45 + 0.55 * up );
        const pg = cl.g.createRadialGradient( px, py - pr * 0.25, 0, px, py, pr );
        pg.addColorStop( 0, this.rgba( this.mix( tone, white, 0.4 ), 0.95 ) );
        pg.addColorStop( 0.7, this.rgba( tone, 0.7 ) );
        pg.addColorStop( 1, this.rgba( tone, 0 ) );
        cl.g.fillStyle = pg;
        for ( const dx of [ -W, 0, W ] ) {
          cl.g.save();
          cl.g.translate( dx, 0 );
          cl.g.fillRect( px - pr, py - pr, pr * 2, pr * 2 );
          cl.g.restore();
        }
      }
    }
    this.clouds = cl.c;

    // the water: turquoise, paler far off, deepening toward us, soft lighter bands along it
    const sea = g.createLinearGradient( 0, hz, 0, H );
    sea.addColorStop( 0, this.rgba( this.mix( aqua, blue, 0.35 ), 1 ) );
    sea.addColorStop( 0.4, this.rgba( this.mix( blue, [ 20, 170, 180 ], 0.5 ), 1 ) );
    sea.addColorStop( 1, this.rgba( this.mix( blue, [ 10, 130, 150 ], 0.55 ), 1 ) );
    g.fillStyle = sea;
    g.fillRect( 0, hz, W, H - hz );
    const wr = this.seededLocal( 37 );
    for ( let i = 0; i < 80; i++ ) {
      const d = Math.pow( wr(), 1.2 );
      const y = hz + ( H - hz ) * ( 0.02 + d * 0.96 );
      g.save();
      g.translate( wr() * W, y );
      g.scale( ( 30 + 140 * d ) * k, ( 1 + 4 * d ) * k );
      const lg = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      const tone = wr() < 0.6 ? this.mix( aqua, white, 0.35 ) : this.mix( blue, [ 0, 90, 120 ], 0.3 );
      lg.addColorStop( 0, this.rgba( tone, 0.28 ) );
      lg.addColorStop( 1, this.rgba( tone, 0 ) );
      g.fillStyle = lg;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }

    // the Bosphorus Bridge in true perspective (owner: think 3D, as seen from the sea): a camera on the
    // water 1600 m off the bridge and 30 m up, looking along the strait. Real sizes: main span 1074 m,
    // towers 165 m above the sea, deck 64 m up and 33 m wide, side spans 231 m and 255 m. Each tower's
    // two legs stand one behind the other across the deck, so from the sea we see the tower from its
    // side, the far leg just inside the near one; the deck passes between them, its underside showing
    // since we are below it. The shores run along the strait to the vanishing point; the towers stand
    // on land at the water's edge.
    const D = 1600, eye = 30;
    const F = UW * 0.4 * D / 537;
    const P = ( x: number, y: number, z: number ): [ number, number ] => [ cx + F * x / ( D + z ), hz - F * ( y - eye ) / ( D + z ) ];
    const steel = this.mix( this.mix( [ 140, 156, 170 ], blue, 0.12 ), white, 0.15 );
    const steelDark = this.mix( steel, [ 70, 85, 100 ], 0.3 );
    const towerH = 165, deckH = 64, halfW = 16.5, shoreM = 515;
    // the land: rising from the water's edge away from the strait, painted strip by strip from the far
    // end toward us; the far strips paler in the haze. The strips behind the bridge on the back layer,
    // the ones in front of it on the front layer (so the boats pass behind them)
    // the shore: a stone quay 5 m high at the water, a level terrace round the tower, then the hillside
    const lift = ( ax: number ) => { const f = Math.min( 1, Math.max( 0, ( ax - 600 ) / 650 ) ); return 5 + 120 * ( 1 - Math.pow( 1 - f, 2 ) ); };
    const landC: RGB = this.mix( this.mix( [ 110, 145, 105 ], aqua, 0.15 ), white, 0.1 );
    const fr = canvas();
    const f = fr.g;
    const lr2 = this.seededLocal( 47 );
    // the shore line along the strait (straight, as it was)
    const shoreAt = ( z: number ) => shoreM + 0 * z;
    const quayEnd = F * shoreM / ( F * shoreM / ( D + 3000 ) + 20 ) - D;
    // the waterfront: on the Bosphorus the houses at the water are yalıs, the old wooden mansions on
    // their stone bases, and here and there a palace (owner); along each shore, gaps between them.
    // Their colours as Dock Town's Istanbul has them
    type Bld = { side: number; z0: number; z1: number; deep: number; h: number; palace: boolean; wall: RGB; roof: RGB; floors: number };
    const blds: Bld[] = [];
    const br = this.seededLocal( 53 );
    const yWalls: RGB[] = [ [ 158, 58, 46 ], [ 242, 238, 230 ], [ 232, 217, 181 ], [ 215, 196, 142 ], [ 201, 207, 196 ], [ 227, 195, 184 ] ];
    const yRoofs: RGB[] = [ [ 142, 70, 54 ], [ 169, 82, 60 ], [ 156, 90, 68 ], [ 110, 102, 98 ], [ 185, 94, 68 ] ];
    for ( const side of [ -1, 1 ] ) {
      // a palace on each shore: Çırağan on the European side, Beylerbeyi by the bridge on the Asian
      const pz = side < 0 ? -520 : 120;
      blds.push( { side, z0: pz, z1: pz + ( side < 0 ? 220 : 160 ), deep: 30, h: 15, palace: true, wall: [ 244, 242, 236 ], roof: [ 200, 200, 196 ], floors: 2 } );
      // none past the far tip of the land, where they would stand in the water (owner)
      for ( let z = -1400; z < quayEnd - 60; ) {
        const len = 22 + br() * 16;
        if ( !blds.some( b => b.side === side && b.palace && z + len > b.z0 - 8 && z < b.z1 + 8 ) && br() < 0.7 ) {
          const floors = 2 + ( br() < 0.45 ? 1 : 0 );
          blds.push( { side, z0: z, z1: z + len, deep: 14 + br() * 6, h: floors * 4 + 1.5, palace: false, wall: yWalls[ Math.floor( br() * yWalls.length ) ], roof: yRoofs[ Math.floor( br() * yRoofs.length ) ], floors } );
        }
        z += len + 6 + br() * 30;
      }
    }
    // and one more off each far end (owner)
    for ( const side of [ -1, 1 ] ) {
      const far = blds.filter( b => b.side === side && !b.palace ).sort( ( p2, q ) => q.z0 - p2.z0 )[ 0 ];
      if ( far ) { blds.splice( blds.indexOf( far ), 1 ); }
    }
    // one waterfront building's stretch between zA and zB: its stone base at the water, the facade toward
    // the strait with its rows of windows (a palace: columns, arched windows, its balustrade), its near
    // end, and the roof (a yalı: a low hipped roof with deep eaves)
    const bld = ( c2: CanvasRenderingContext2D, b: Bld, zA: number, zB: number, hazeF: number ) => {
      const xi = b.side * ( shoreAt( zA ) + 2 ), xo = b.side * ( shoreAt( zA ) + 2 + b.deep );
      const gh = 5;
      const poly = ( pts: [ number, number ][], fill: string ) => {
        c2.fillStyle = fill;
        c2.beginPath();
        pts.forEach( ( [ x, y ], i ) => i ? c2.lineTo( x, y ) : c2.moveTo( x, y ) );
        c2.closePath();
        c2.fill();
      };
      const hz2 = ( c: RGB ) => this.mix( c, white, hazeF );
      const wallLit = this.rgba( hz2( b.wall ), 1 ), wallDark = this.rgba( hz2( this.mix( b.wall, [ 90, 80, 76 ], 0.22 ) ), 1 );
      // the stone base at the water
      poly( [ P( xi, 0, zA ), P( xi, gh, zA ), P( xi, gh, zB ), P( xi, 0, zB ) ], this.rgba( hz2( [ 196, 190, 178 ] ), 1 ) );
      // the near end, toward us (only at the building's own near end)
      if ( zA <= b.z0 + 0.01 ) {
        poly( [ P( xi, gh, zA ), P( xi, gh + b.h, zA ), P( xo, gh + b.h, zA ), P( xo, gh, zA ) ], wallDark );
      }
      // the facade toward the strait
      poly( [ P( xi, gh, zA ), P( xi, gh + b.h, zA ), P( xi, gh + b.h, zB ), P( xi, gh, zB ) ], wallLit );
      const [ , ya ] = P( xi, 0, zA ), [ , yb2 ] = P( xi, 4, zA );
      const pxPerM = Math.abs( ya - yb2 ) / 4;
      if ( pxPerM > 0.35 ) {
        const glass = this.rgba( hz2( this.mix( [ 60, 78, 96 ], b.wall, 0.2 ) ), 1 );
        const frame = this.rgba( hz2( this.mix( b.wall, white, 0.55 ) ), 1 );
        const step = b.palace ? 6 : 3.2;
        for ( let zz = Math.ceil( zA / step ) * step + step / 2; zz < zB - 1; zz += step ) {
          for ( let f = 0; f < b.floors; f++ ) {
            const y0 = gh + 1.2 + f * ( b.h - 1.5 ) / b.floors, y1 = y0 + ( b.h - 1.5 ) / b.floors * 0.62;
            if ( pxPerM > 0.8 ) { poly( [ P( xi, y0 - 0.3, zz - 0.95 ), P( xi, y1 + 0.3, zz - 0.95 ), P( xi, y1 + 0.3, zz + 0.95 ), P( xi, y0 - 0.3, zz + 0.95 ) ], frame ); }
            poly( [ P( xi, y0, zz - 0.7 ), P( xi, y1, zz - 0.7 ), P( xi, y1, zz + 0.7 ), P( xi, y0, zz + 0.7 ) ], glass );
          }
          if ( b.palace && pxPerM > 0.5 ) {
            // a pilaster between the bays
            poly( [ P( xi, gh, zz + 2.6 ), P( xi, gh + b.h, zz + 2.6 ), P( xi, gh + b.h, zz + 3.4 ), P( xi, gh, zz + 3.4 ) ], this.rgba( hz2( [ 255, 255, 252 ] ), 1 ) );
          }
        }
      }
      if ( b.palace ) {
        // the cornice and the balustrade along the top
        poly( [ P( xi, gh + b.h, zA ), P( xi, gh + b.h + 1.5, zA ), P( xi, gh + b.h + 1.5, zB ), P( xi, gh + b.h, zB ) ], this.rgba( hz2( [ 226, 224, 218 ] ), 1 ) );
        poly( [ P( xi, gh + b.h + 1.5, zA ), P( xo, gh + b.h + 1.5, zA ), P( xo, gh + b.h + 1.5, zB ), P( xi, gh + b.h + 1.5, zB ) ], this.rgba( hz2( [ 214, 212, 206 ] ), 1 ) );
      } else {
        // the deep eaves and the low hipped roof
        const ov = 1.4, rh = 3.2, xm = ( xi + xo ) / 2;
        const roofC = hz2( b.roof );
        poly( [ P( xi - b.side * ov, gh + b.h, zA - ( zA <= b.z0 + 0.01 ? ov : 0 ) ), P( xm, gh + b.h + rh, zA + ( zA <= b.z0 + 0.01 ? 3 : 0 ) ), P( xm, gh + b.h + rh, zB ), P( xi - b.side * ov, gh + b.h, zB ) ], this.rgba( roofC, 1 ) );
        if ( zA <= b.z0 + 0.01 ) {
          poly( [ P( xi - b.side * ov, gh + b.h, zA - ov ), P( xm, gh + b.h + rh, zA + 3 ), P( xo + b.side * ov, gh + b.h, zA - ov ) ], this.rgba( this.mix( roofC, [ 60, 30, 24 ], 0.2 ), 1 ) );
        }
        // the shade under the eaves
        poly( [ P( xi, gh + b.h - 0.6, zA ), P( xi, gh + b.h, zA ), P( xi, gh + b.h, zB ), P( xi, gh + b.h - 0.6, zB ) ], this.rgba( hz2( this.mix( b.wall, [ 40, 30, 30 ], 0.35 ) ), 1 ) );
      }
    };
    const strip = ( c2: CanvasRenderingContext2D, z: number, dz: number ) => {
      const sh = shoreAt( z ), sh2 = shoreAt( z - dz );
      const hazeF = Math.min( 0.7, Math.max( 0, z / 4000 ) * 0.7 );
      for ( const side of [ -1, 1 ] ) {
        c2.beginPath();
        const pts: [ number, number ][] = [];
        // past the end of the stone quay the land's foot keeps to where the quay ends on screen, so the
        // tip runs over the quay's end instead of on past it; the hills above keep their shape (owner)
        const tipX = z > quayEnd ? ( F * shoreM / ( D + quayEnd ) ) * ( D + z ) / F : sh;
        const start = Math.max( sh, tipX );
        const startB = z - dz > quayEnd ? Math.max( sh2, ( F * shoreM / ( D + quayEnd ) ) * ( D + z - dz ) / F ) : sh2;
        for ( let ax = start; ax <= 4000 + sh; ax += 40 ) { pts.push( P( side * ax, lift( ax - sh + shoreM ), z ) ); }
        c2.moveTo( ...P( side * startB, 0, z - dz ) );
        for ( const p2 of pts ) { c2.lineTo( p2[ 0 ], p2[ 1 ] ); }
        const far = P( side * 4000, 0, z - dz );
        c2.lineTo( side < 0 ? Math.min( far[ 0 ], -10 ) : Math.max( far[ 0 ], W + 10 ), pts[ pts.length - 1 ][ 1 ] );
        c2.lineTo( side < 0 ? -10 : W + 10, P( 0, 0, z - dz )[ 1 ] + 2 );
        c2.lineTo( ...P( side * startB, 0, z - dz ) );
        c2.closePath();
        const shade = 0.12 * Math.sin( z / 90 + side );
        // the quay's stone face toward the water
        const q0 = P( side * sh, 0, z ), q1 = P( side * sh2, 0, z - dz ), q2 = P( side * sh2, 5, z - dz ), q3 = P( side * sh, 5, z );
        c2.fillStyle = this.rgba( this.mix( this.mix( landC, [ 70, 105, 80 ], 0.15 + shade ), white, hazeF ), 1 );
        c2.fill();
        // the pale stone quay stops 20 px short of the land's far tip (owner)
        if ( z <= quayEnd ) {
          c2.fillStyle = this.rgba( this.mix( this.mix( cream, [ 170, 160, 150 ], 0.4 ), white, hazeF ), 1 );
          c2.beginPath();
          c2.moveTo( ...q0 ); c2.lineTo( ...q1 ); c2.lineTo( ...q2 ); c2.lineTo( ...q3 );
          c2.closePath();
          c2.fill();
        }
        // the yalıs and palaces along the water in this strip
        for ( const b of blds ) {
          if ( b.side !== side || b.z1 <= z - dz || b.z0 >= z ) { continue; }
          bld( c2, b, Math.max( b.z0, z - dz ), Math.min( b.z1, z ), hazeF );
        }
        // houses and trees up the hill behind them
        for ( let i = 0; i < 4; i++ ) {
          const ax = sh + 45 + lr2() * 900;
          const [ hx, hy ] = P( side * ax, lift( ax - sh + shoreM ), z - lr2() * dz );
          if ( hx < -10 || hx > W + 10 ) { continue; }
          const sz = Math.max( 1, F * ( 10 + lr2() * 10 ) / ( D + z ) );
          if ( lr2() < 0.4 ) {
            c2.fillStyle = this.rgba( this.mix( [ 80, 125, 85 ], white, hazeF ), 1 );
            c2.beginPath();
            c2.ellipse( hx, hy - sz * 0.4, sz * 0.8, sz * 0.6, 0, 0, Math.PI * 2 );
            c2.fill();
          } else {
            this.house( c2, hx, hy, sz, hazeF, side, lr2 );
          }
        }
      }
    };
    // down to the bridge's own line, so no strip of water shows round the towers' feet (owner)
    for ( let z = 3000; z > 0; z -= 30 + z * 0.03 ) { strip( g, z, Math.min( z, 30 + z * 0.03 ) ); }
    // the water's edge along both shores, a pale stone line
    const edge = ( c2: CanvasRenderingContext2D, z0: number, z1: number ) => {
      c2.strokeStyle = this.rgba( this.mix( cream, white, 0.3 ), 0.9 );
      c2.lineWidth = Math.max( 1, 1.2 * k );
      for ( const side of [ -1, 1 ] ) {
        c2.beginPath();
        for ( let z = z0; ( z1 < z0 ) ? z >= z1 : z <= z1; z += ( z1 < z0 ? -20 : 20 ) ) { c2.lineTo( ...P( side * shoreAt( z ), 0, z ) ); }
        c2.stroke();
      }
    };
    edge( g, quayEnd, 0 );

    // the bridge, from the back to the front
    // the deck is not dead level (owner): it rises gently to the middle of the main span, a few metres,
    // and runs down a little over the side spans toward the land
    const deckAt = ( m: number ) => {
      const a2 = Math.abs( m );
      if ( a2 <= 537 ) { const t = m / 537; return deckH + 6 * ( 1 - t * t ); }
      return deckH - Math.min( 10, ( a2 - 537 ) * 0.012 );
    };
    const cableAt = ( m: number ) => {
      const a2 = Math.abs( m );
      if ( a2 <= 537 ) { const t = m / 537; return deckAt( m ) + 3 + ( towerH - 3 - deckAt( m ) - 3 ) * t * t; }
      const span = m < 0 ? 231 : 255;
      const t = Math.min( 1, ( a2 - 537 ) / span );
      return towerH - 3 - ( towerH - 3 - deckAt( m ) ) * ( t * 0.96 + t * t * 0.04 );
    };
    const polyF = ( pts: [ number, number ][], fill: string | CanvasGradient ) => {
      g.fillStyle = fill;
      g.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.fill();
    };
    // a tower leg: a box 7 m across, 5 m deep, from the ground to the top, lit red; its face toward us
    // and its face toward the middle of the strait both show
    const leg = ( tx: number, z: number, pale: number ) => {
      const base = lift( Math.abs( tx ) ) + 8;
      // the bridge's own pale steel (owner)
      // lighter (owner)
      const lit = ( a2: number ) => this.rgba( this.mix( this.mix( steel, steelDark, a2 * 0.35 ), white, 0.3 + pale ), 1 );
      const inner = tx < 0 ? 3.5 : -3.5;
      const [ ax0, ay0 ] = P( tx - 3.5, base, z - 2.5 ), [ ax1 ] = P( tx + 3.5, base, z - 2.5 );
      const [ bx0, by0 ] = P( tx - 3.2, towerH, z - 2.5 ), [ bx1 ] = P( tx + 3.2, towerH, z - 2.5 );
      const grad = g.createLinearGradient( 0, ay0, 0, by0 );
      grad.addColorStop( 0, lit( 0.65 ) );
      grad.addColorStop( 0.55, lit( 0.42 ) );
      grad.addColorStop( 1, lit( 0.75 ) );
      // the side face toward the middle, a shade darker
      const sideA = P( tx + inner, base, z - 2.5 ), sideB = P( tx + inner * 0.92, towerH, z - 2.5 );
      const sideC = P( tx + inner * 0.92, towerH, z + 2.5 ), sideD = P( tx + inner, base, z + 2.5 );
      polyF( [ sideA, sideB, sideC, sideD ], this.rgba( this.mix( steelDark, white, 0.25 + pale ), 1 ) );
      polyF( [ [ ax0, ay0 ], [ bx0, by0 ], [ bx1, by0 ], [ ax1, ay0 ] ], grad );
    };
    // the beams joining a tower's two legs across the deck, seen end on
    const beam = ( tx: number, h: number ) => {
      polyF( [ P( tx - 3.2, h, -halfW ), P( tx + 3.2, h, -halfW ), P( tx + 3.2, h, halfW ), P( tx - 3.2, h, halfW ) ],
        this.rgba( this.mix( steel, white, 0.08 ), 1 ) );
      polyF( [ P( tx - 3.2, h, -halfW ), P( tx + 3.2, h, -halfW ), P( tx + 3.2, h - 5, -halfW ), P( tx - 3.2, h - 5, -halfW ) ],
        this.rgba( steelDark, 1 ) );
    };
    const cable = ( z: number, pale: number ) => {
      const c2 = this.mix( steel, white, pale );
      g.strokeStyle = this.rgba( c2, 0.3 );
      g.lineWidth = Math.max( 0.5, 0.6 * k );
      for ( let m = -537 + 16; m < 537; m += 16 ) {
        g.beginPath();
        g.moveTo( ...P( m, cableAt( m ), z ) );
        g.lineTo( ...P( m, deckAt( m ), z ) );
        g.stroke();
      }
      g.strokeStyle = this.rgba( c2, 1 );
      g.lineWidth = Math.max( 1, 1.2 * k * D / ( D + z ) );
      g.beginPath();
      for ( let m = -537 - 231; m <= 537 + 255; m += 4 ) { g.lineTo( ...P( m, cableAt( m ), z ) ); }
      g.stroke();
    };
    // behind the deck: the far legs, the far cable and its hangers, the beam under the deck
    for ( const m of [ -537, 537 ] ) { leg( m, halfW, 0.15 ); }
    cable( halfW, 0.15 );
    for ( const m of [ -537, 537 ] ) { beam( m, deckH - 8 ); }
    // the anchorages on the land where the cables come down
    for ( const m of [ -537 - 231, 537 + 255 ] ) {
      const gh = lift( Math.abs( m ) );
      polyF( [ P( m - 25, gh, -halfW - 4 ), P( m - 25, deckH + 6, -halfW - 4 ), P( m + 25, deckH + 6, -halfW - 4 ), P( m + 25, gh, -halfW - 4 ) ],
        this.rgba( this.mix( steel, cream, 0.3 ), 1 ) );
    }
    // the deck: its underside (we are below it), then its near face; piers under it over the land
    for ( let m = 600; m < 3000; m += 60 ) {
      for ( const side of [ -1, 1 ] ) {
        const gh = lift( m ), dh = deckAt( side * m ) - 3;
        if ( gh >= dh - 1 ) { continue; }
        polyF( [ P( side * m - 2, gh, -2 ), P( side * m - 2, dh, -2 ), P( side * m + 2, dh, -2 ), P( side * m + 2, gh, -2 ) ], this.rgba( steelDark, 1 ) );
      }
    }
    const ms: number[] = [];
    for ( let m = -3000; m <= 3000; m += 12 ) { ms.push( m ); }
    const under: [ number, number ][] = [ ...ms.map( m => P( m, deckAt( m ) - 3, -halfW ) ), ...ms.slice().reverse().map( m => P( m, deckAt( m ) - 3, halfW ) ) ];
    polyF( under, this.rgba( this.mix( steelDark, white, 0.1 ), 1 ) );
    const face: [ number, number ][] = [ ...ms.map( m => P( m, deckAt( m ), -halfW ) ), ...ms.slice().reverse().map( m => P( m, deckAt( m ) - 3, -halfW ) ) ];
    polyF( face, this.rgba( steel, 1 ) );
    // in front of the deck: the beams above it, the near cable and its hangers, the near legs
    for ( const m of [ -537, 537 ] ) { beam( m, towerH - 2 ); beam( m, towerH * 0.62 ); }
    cable( -halfW, 0 );
    for ( const m of [ -537, 537 ] ) {
      leg( m, -halfW, 0 );
      // its foundation: a concrete block 30 m by 50 m, 8 m high, standing on the terrace by the quay;
      // its top, its face toward us and its face toward the strait
      const gh = lift( 537 ), top = gh + 8;
      const inner = m < 0 ? 15 : -15;
      const conc: RGB = this.mix( cream, steel, 0.35 );
      polyF( [ P( m - 15, top, -25 ), P( m + 15, top, -25 ), P( m + 15, top, 25 ), P( m - 15, top, 25 ) ], this.rgba( this.mix( conc, white, 0.25 ), 1 ) );
      polyF( [ P( m + inner, gh, -25 ), P( m + inner, top, -25 ), P( m + inner, top, 25 ), P( m + inner, gh, 25 ) ], this.rgba( this.mix( conc, steelDark, 0.25 ), 1 ) );
      polyF( [ P( m - 15, gh, -25 ), P( m - 15, top, -25 ), P( m + 15, top, -25 ), P( m + 15, gh, -25 ) ], this.rgba( conc, 1 ) );
    }
    // the land in front of the bridge, coming toward us, on the front layer
    for ( let z = 0; z > -1500; z -= 18 ) { strip( f, z, 18 ); }
    edge( f, 0, -1500 );

    // a light clearing behind the hero's words
    for ( const b of this.blocks ) {
      g.save();
      g.translate( b.x + b.w / 2, b.y + b.h / 2 );
      g.scale( b.w / 2 + 70 * k, b.h / 2 + 40 * k );
      const cl = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      cl.addColorStop( 0, this.rgba( white, 0.2 ) );
      cl.addColorStop( 1, this.rgba( white, 0 ) );
      g.fillStyle = cl;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    this.back = c;

    // a small lighthouse at the near end of the left shore (owner): a white round tower on its stone
    // base, the dark gallery, the red lantern room with its cap
    {
      const lz = -1400 + 0;
      const [ , ly ] = P( -560, 0, lz );
      const yz = ( F * eye / ( H * 0.93 - hz ) ) - D;
      const zz = Math.max( -1500, Math.min( -200, yz ) );
      const lp = ( x: number, y: number ) => P( -560 + x, lift( 560 ) + y, zz );
      const lpoly = ( pts: [ number, number ][], fill: string | CanvasGradient ) => {
        f.fillStyle = fill;
        f.beginPath();
        pts.forEach( ( [ x, y ], i ) => i ? f.lineTo( x, y ) : f.moveTo( x, y ) );
        f.closePath();
        f.fill();
      };
      void ly;
      lpoly( [ lp( -4, 0 ), lp( -4, 2 ), lp( 4, 2 ), lp( 4, 0 ) ], this.rgba( this.mix( cream, [ 170, 160, 150 ], 0.4 ), 1 ) );
      const bodyG = f.createLinearGradient( lp( -2.2, 0 )[ 0 ], 0, lp( 2.2, 0 )[ 0 ], 0 );
      bodyG.addColorStop( 0, 'rgb(252,252,250)' );
      bodyG.addColorStop( 1, 'rgb(212,216,218)' );
      lpoly( [ lp( -2.2, 2 ), lp( -1.6, 14 ), lp( 1.6, 14 ), lp( 2.2, 2 ) ], bodyG );
      lpoly( [ lp( -2.4, 14 ), lp( -2.4, 14.8 ), lp( 2.4, 14.8 ), lp( 2.4, 14 ) ], 'rgb(50,56,62)' );
      lpoly( [ lp( -1.4, 14.8 ), lp( -1.4, 17 ), lp( 1.4, 17 ), lp( 1.4, 14.8 ) ], 'rgb(206,28,32)' );
      lpoly( [ lp( -1.7, 17 ), lp( 0, 18.6 ), lp( 1.7, 17 ) ], 'rgb(150,20,24)' );
    }
    this.project = P;
    this.deckAtM = deckAt;
    this.front = fr.c;
  }

  /**
   * One of the city's houses on the shore, in more detail (owner): one to five storeys, its front lit,
   * the side toward the strait a shade darker; rows of windows with their light frames; a hipped roof of
   * red tiles, or a flat roof with its parapet on the taller blocks; hazier far off. `x, y` its foot,
   * `sz` its size at that distance, `side` which shore (so the side face shows toward the strait).
   */
  private house( c: CanvasRenderingContext2D, x: number, y: number, sz: number, haze: number, side: number, r: () => number ): void {
    const [ , , cream, terra, red ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    const walls: RGB[] = [ cream, this.mix( cream, white, 0.6 ), this.mix( red, cream, 0.45 ), this.mix( cream, terra, 0.35 ), this.mix( cream, [ 214, 196, 150 ], 0.5 ), [ 236, 232, 226 ] ];
    const wall = this.mix( walls[ Math.floor( r() * walls.length ) ], white, haze );
    const storeys = 1 + Math.floor( Math.pow( r(), 1.6 ) * 5 );
    const w = sz * ( 0.8 + r() * 0.6 ), sw = w * 0.28;
    const fh = sz * 0.36, h = fh * storeys + sz * 0.12;
    const flat = storeys >= 3 && r() < 0.6;
    const x0 = x - w / 2;
    // the side face toward the strait, then the front
    c.fillStyle = this.rgba( this.mix( wall, [ 120, 110, 105 ], 0.22 ), 1 );
    if ( side < 0 ) { c.fillRect( x0 + w, y - h, sw, h ); } else { c.fillRect( x0 - sw, y - h, sw, h ); }
    c.fillStyle = this.rgba( wall, 1 );
    c.fillRect( x0, y - h, w, h );
    // the windows, row by row, once the house is big enough to show them
    if ( sz > 5 ) {
      const cols = Math.max( 1, Math.floor( w / ( sz * 0.26 ) ) );
      const ww = w / cols * 0.42, wh = fh * 0.45;
      const glass = this.mix( this.mix( [ 70, 90, 110 ], wall, 0.25 ), white, haze );
      const frame = this.mix( wall, white, 0.5 );
      for ( let f = 0; f < storeys; f++ ) {
        const wy = y - sz * 0.06 - fh * ( f + 1 ) + fh * 0.28;
        for ( let q = 0; q < cols; q++ ) {
          const wx = x0 + ( q + 0.5 ) * w / cols - ww / 2;
          if ( sz > 9 ) { c.fillStyle = this.rgba( frame, 1 ); c.fillRect( wx - 0.6, wy - 0.6, ww + 1.2, wh + 1.2 ); }
          c.fillStyle = this.rgba( glass, 1 );
          c.fillRect( wx, wy, ww, wh );
        }
      }
    }
    if ( flat ) {
      // a flat roof with its parapet
      c.fillStyle = this.rgba( this.mix( wall, [ 150, 140, 135 ], 0.3 ), 1 );
      c.fillRect( x0 - w * 0.02, y - h - sz * 0.05, w * 1.04, sz * 0.05 );
    } else {
      // a hipped roof of red tiles, its far slope darker
      const tile = this.mix( this.mix( terra, red, 0.45 ), white, haze );
      const rh = sz * 0.28;
      c.fillStyle = this.rgba( tile, 1 );
      c.beginPath();
      c.moveTo( x0 - w * 0.06, y - h );
      c.lineTo( x0 + w * 0.22, y - h - rh );
      c.lineTo( x0 + w * 0.78, y - h - rh );
      c.lineTo( x0 + w * 1.06, y - h );
      c.closePath();
      c.fill();
      c.fillStyle = this.rgba( this.mix( tile, [ 80, 40, 30 ], 0.25 ), 1 );
      c.beginPath();
      if ( side < 0 ) {
        c.moveTo( x0 + w * 1.06, y - h ); c.lineTo( x0 + w * 0.78, y - h - rh ); c.lineTo( x0 + w + sw * 0.7, y - h - rh * 0.7 ); c.lineTo( x0 + w + sw, y - h );
      } else {
        c.moveTo( x0 - w * 0.06, y - h ); c.lineTo( x0 + w * 0.22, y - h - rh ); c.lineTo( x0 - sw * 0.7, y - h - rh * 0.7 ); c.lineTo( x0 - sw, y - h );
      }
      c.closePath();
      c.fill();
    }
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Istanbul's own: aqua, Bosphorus blue, cream, terracotta, rose red. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 163, 221, 229 ], [ 33, 156, 202 ], [ 239, 234, 208 ], [ 184, 121, 88 ], [ 184, 81, 77 ] ];
  }
}
